import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Button, Box, IconButton, Stack } from '@mui/material';
import Carousel from 'react-multi-carousel';
import GameV2 from './GameV2';

const GameListV2 = ({
  games,
  currentUser,
  cancelGame,
  useCarousel = false,
  useGrid = false,
  isOrganiser = false,
  isPending = false,
  isPast = false,
  hideCTA = false,
}) => {
  const navigate = useNavigate();

  const GameBox = ({ game }) => {
    return (
      <Box sx={{ width: { xs: '100%', sm: '326px' }, maxWidth: '100%', mx: { xs: 0, sm: 1 } }}>
        <GameV2
          data={game}
          isOrganiser={isOrganiser}
          isPending={isPending}
          isPast={isPast}
          hideCTA={hideCTA}
        ></GameV2>
        {game.createdBy.id === currentUser.uid && game.active && (
          <>
            <Button
              variant="gameDelete"
              color="error"
              size="small"
              onClick={() => {
                cancelGame(game);
              }}
            >
              Cancel Game
            </Button>
            <Button
              variant="gameInvite"
              color="primary"
              size="small"
              onClick={() => {
                navigate(`/games/${game.id}/invite`);
              }}
            >
              Invite
            </Button>
          </>
        )}
      </Box>
    );
  };

  const ButtonGroup = ({ next, previous, ...rest }) => {
    const {
      carouselState: { currentSlide },
    } = rest;
    return (
      <Box
        className="carousel-button-group"
        sx={{
          position: 'absolute',
          top: { xs: '-54px', sm: '-50px' },
          left: { xs: 2, sm: 'unset' },
          right: { xs: 'unset', sm: 0 },
          display: 'flex',
          gap: 0.5,
        }}
      >
        <IconButton 
          aria-label="previous" 
          disabled={currentSlide === 0} 
          onClick={() => previous()}
          sx={{ 
            width: { xs: 36, sm: 48 }, 
            height: { xs: 36, sm: 48 } 
          }}
        >
          <ArrowBackIcon fontSize="medium" />
        </IconButton>
        <IconButton
          aria-label="next"
          disabled={currentSlide === games.length - 1}
          onClick={() => next()}
          sx={{ 
            width: { xs: 36, sm: 48 }, 
            height: { xs: 36, sm: 48 } 
          }}
        >
          <ArrowForwardIcon fontSize="medium" />
        </IconButton>
      </Box>
    );
  };

  const carouselResponsive = {
    desktop: {
      breakpoint: { max: 3000, min: 1024 },
      items: 3,
      slidesToSlide: 2,
    },
    tablet: {
      breakpoint: { max: 1024, min: 464 },
      items: 2,
      slidesToSlide: 2,
    },
    mobile: {
      breakpoint: { max: 464, min: 0 },
      items: 1,
      slidesToSlide: 1,
      partialVisibilityGutter: 40, // Show 40px of the next card on mobile
    },
  };

  const carouselRef = useRef(null);

  if (useCarousel) {
    const extraSlidesLength = Math.floor(games.length / 3);
    const extraSlides = [];
    for (var i = 0; i < extraSlidesLength; i++) {
      extraSlides.push(
        <Box
          className="extra_slide"
          key={`emptySlide-${i}`}
          sx={{ mr: 2, width: { xs: '100vw', md: 'unset' }, minWidth: '326px' }}
        ></Box>
      );
    }

    return (
      <Box sx={{ position: 'relative', width: '100%', py: 2 }}>
        <Box
          sx={{
            width: { xs: 'calc(100vw - 48px)', md: 'calc(75vw - 48px)' },
            maxWidth: '1012px',
          }}
        >
          <Carousel
            ref={carouselRef}
            responsive={carouselResponsive}
            showDots={false}
            keyBoardControl={true}
            arrows={false}
            swipeable
            draggable
            renderArrowsWhenDisabled
            renderButtonGroupOutside={true}
            customButtonGroup={<ButtonGroup />}
            partialVisible={true}
          >
            {games.map((game, index) => (
              <Box 
                sx={{ 
                  pr: { xs: 2, sm: 2 }, 
                  width: { xs: '100%', sm: 'auto' },
                  maxWidth: '100%',
                }} 
                key={`gameSlide-${index}`}
              >
                <GameV2
                  data={game}
                  isOrganiser={isOrganiser}
                  isPending={isPending}
                  isPast={isPast}
                  hideCTA={hideCTA}
                ></GameV2>
              </Box>
            ))}
            {extraSlides}
          </Carousel>
        </Box>
      </Box>
    );
  }

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 4 }}>
      {games.map((game, index) => (
        <GameBox game={game} key={`gameBox-${index}`} />
      ))}
    </Stack>
  );
};

export default GameListV2;
